/**
 * ------ maintenance history ------
 * 05/28/2019   Yoyo Fang   add single object configutation, add ability to pass-in search keyword
 * 05/29/2019   Yoyo Fang   Config context menu items for research-refineby, group init code by component
 * 06/17/2019   Yoyo Fang   change to on push
 * 08/29/2019   Bowen Li    change relationship type
 * 09/11/2019   Bowen Li    remove relationship
 */
import { Component, OnInit, Input, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription, Observable, Subject } from 'rxjs';
import { map, take, filter, debounceTime, auditTime } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import { KeywordSearchConfig } from '../../widgets/keyword-search/keyword-search.component';
import { FinderConfig, FinderSetting, ResearchFocusStatus, FinderActions } from '../../components/finder/finder.view-model';
import { RelationshipTreeConfig, RelationshipTreeEvents, RelationshipTreeNode, RelationshipTreeActions } from '../../components/relationship-tree/relationship-tree.view-model';
import { ResearchLayoutConfig, ResearchLayoutActions, ResearchLayoutEvents } from '../../components/research-layout/research-layout.data';
import { ThreadDetailConfig, ThreadDetailActions } from '../../components/thread-detail/thread-detail.view-model';
import { ThreadFilterConfig, ThreadFilterEventType, OPEN_DIALOG } from '../../components/thread-filter/thread-filter.view-model';
import {
    ResearchScreenActionTypes, ResearchScreenSetLayoutMode, ResearchScreenSetLayoutCollapse,
    ResearchScreenSetKeyWord, ResearchScreenInitializeFocus, ResearchScreenSetRelationship,
    ResearchScreenSetRelationshipCheckedKeys, ResearchScreenSetRelationshipNameFormat,
    ResearchScreenSetFocusStatus, ResearchScreenSetSortInfo, ResearchScreenThreadListRemoveAttachment,
    ResearchScreenThreadListRemoveSidenote, ResearchScreenHideEntityFocus,
    ResearchScreenHideRelationshipTree, ResearchScreenSetRelationshipNodes,
    ResearchScreenSetActiveTab, ResearchScreenSetDisplayInfo
} from '../../redux/actions/research-screen.actions';
import {
    getDynamicResearchLayoutSelector2, getDynamicResearchFocusSelector2,
    getDynamicResearchQueryInfoSelector2, getDynamicResearchThreadListSelector2,
    getDynamicResearchThreadListItemSelector2, getDynamicResearchRelationshipSelector2,
    getDynamicResearchRelationshipTreeNodesSelector2, getDynamicResearchRelationshipTreeCheckedKeysSelector2, getDynamicResearchThreadListtMatchedAttachmentSelector2, getDynamicResearchActiveThreadSelector2,
    getDynamicResearchKeywordSelector2, getDynamicResearchSortInfoSelector2,
    getDynamicResearchGridStatusSelector2, getDynamicResearchFilterEnableSelector2,
    getDynamicResearchFilterItemsSelector2, getDynamicResearchFilterLogicSelector2,
    getDynamicResearchRefinebyDataSelector, getDynamicResearchRefinebyCategorySelector,
    getDynamicResearchRefinebySelectedValueSelector, getDynamicResearchRefinebyStatusSelector, getDynamicResearchRefinebyCategoryFilterSelector, getDynamicResearchDisplaySelector2,
    getDynamicResearchThreadListGroupItemSelector, getDynamicResearchThreadListGroupItemsSelector, getDynamicResearchThreadListGridGropLayoutSelector
} from '../../redux/reducers/research-screen.reducer';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { QueryInfo, ThreadListConfig, SortInfo, ResearchRefinebyConfig } from '../../pages/research/research.view-model';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { StoreQuerierService } from '../../services/store-querier.service';
import { ResearchHelperService } from '../../services/research-helper.service';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';

import { EntityService } from '../../tamalelibs/services/entity.service';
import { Entity } from '../../tamalelibs/models/entity.model';
import { EntityBrief } from '../../tamalelibs/models/entity-brief.model';
import { DataList } from '../../tamalelibs/models/data-list.model';
import { FilterService } from '../../tamalelibs/services/filter.service';
import { TabOptions, TabItem } from '../../widgets/tabs/tabs.model';
import {
    UPDATE_NOTE_SUCCESS, CREATE_NOTE_SUCCESS, DELETE_NOTE_SUCCESS,
    DELETE_SIDENOTE_SUCCESS, DELETE_NOTE_FAILURE, DELETE_SIDENOTE_FAILURE, DELETE_ATTACHMENT_SUCCESS, DELETE_NOTES_SUCCESS, DELETE_EVENT_SUCCESS,
    UPDATE_EVENT_SUCCESS
} from '../../tamalelibs/redux/note.actions';
import { ResearchScreenConfigs, ResearchScreenConfigAction, ResearchScreenConfigActionType, ResearchScreenSubComponents } from './research-screen.model';
import { businessConstants } from '../../constants/business.constants';
import { AppConfig } from '../../tamalelibs/models/app-config.model';
import { StringHelperService } from '../../tamalelibs/services/string-helper.service';
import { EntityType } from '../../tamalelibs/models/entity-type.model';
import { LayoutMenuConfig } from '../layout/layout.model';
import { CHANGE_SHOW_LIST_MODE, OPEN_FILTER_DIALOG, DisplayConfig, ShowAsMode, SHOW_CALENDAR_EVENTS } from '../display/display.model';
import { ThreadEntry } from '../../tamalelibs/models/thread-entry.model';
import { EDIT_CONTACT_SUCCESS } from '../../tamalelibs/redux/app.actions';
import { ThreadFilterService } from '../thread-filter/thread-filter.service';
import { AppendEntitiesDialogService } from '../append-entities-dialog/append-entities-dialog.component.service';
import { AppendEntitiesDialogOpenOptions, AppendEntitiesDialogType } from '../append-entities-dialog/append-entities-dialog.model';

@Component({
    selector: 'research-screen',
    templateUrl: './research-screen.component.html',
    styleUrls: ['./research-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchScreenComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() config: ResearchScreenConfigs;

    isAdminUser = false;
    isComplianceUser = false;
    isEntityFocusHidden = false;
    isRelationshipTreeHidden = false;
    isEntityFocusStatusDisable = false;
    isRelationshipTreeStatusDisable = false;
    isRelationshipLoading = false;
    isThreadDetailLoading = false;
    screenKey: string;
    sortChangeSubject: Subject<any> = new Subject();
    sortInfo: SortInfo = new SortInfo();
    threadListTotalCount$: Observable<any>;

    finderConfig: FinderConfig = new FinderConfig();
    keywordSearchConfig: KeywordSearchConfig;
    relationshipTreeConfig: RelationshipTreeConfig = new RelationshipTreeConfig();
    layoutConfig: ResearchLayoutConfig = new ResearchLayoutConfig(2);
    threadDetailConfig: ThreadDetailConfig;
    displayConfig: DisplayConfig = new DisplayConfig();
    threadFilterConfig: ThreadFilterConfig;
    threadListConfig: ThreadListConfig;
    tabsOptions: TabOptions;
    refinebyConfig: ResearchRefinebyConfig;
    layoutMenuConfig: LayoutMenuConfig = new LayoutMenuConfig();
    currentTab = 0;

    private _currentUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _isAppendEntities = false;
    private _refreshing = false;
    private _previousRelTreeCheckedKeys: Array<string> = [];
    private _queryInfo: QueryInfo = new QueryInfo();
    private _refreshManually = false;
    private _isCreateScreen: Boolean = false;
    private _rowModelType;
    private _showAsMode = ShowAsMode.NOTES;
    private _showCalendarEvents = false;
    private _threadListTotalCount: number;
    private _threadListIDs: string;

    constructor(
        private _appendEntitiesDialogService: AppendEntitiesDialogService,
        private _entityService: EntityService,
        private _filterService: FilterService,
        private _researchHelper: ResearchHelperService,
        private _store: Store<any>,
        private _toast: ToastService,
        private _storeQuerier: StoreQuerierService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _relationshipTreeService: RelationshipTreeService,
        private _threadFilterService: ThreadFilterService,
    ) {
    }

    ngOnInit() {
        if (this.config.advFilter) {
            this._queryInfo.advFilterMap.clear();
            this._queryInfo.advFilterMap.set('fromEmailLink', this.config.advFilter);
        } else {
            if (this._queryInfo.advFilterMap.size === 0 || this._queryInfo.advFilterMap.has('fromEmailLink')
                || !this._queryInfo.advFilterMap.has('default')) {
                this._queryInfo.advFilterMap.clear();
                this._queryInfo.advFilterMap.set('default', AppConfig.defaultAdvFilter);
            }
        }
        this._currentUser = this._storeQuerier.getCurrentUser();
        if (this._currentUser && this._currentUser.credential) {
            this.isAdminUser = this._currentUser.credential.admin;
            this.isComplianceUser = this._currentUser.credential.compliance;
        }
        this.screenKey = this.config.screenKey;
        // same logic in keyword-search.component
        this._queryInfo.searchText = StringHelperService.escapeSearchString(this.config.searchText);
        this._initTabs();
        this._initRefineby();
        this._initThreadFilter();
        this._initThreadList();
        this._initThreadDetail();

        this._setupLayout();
        this._initSubComponentState();
        this._resetRefinebyContextMenu();
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this.threadListConfig.threadListGridGroupLayoutSelector),
            ).subscribe(data => {
                if (data) {
                    // init must before layoutMenuConfig
                    this._rowModelType = data['rowModelType'];
                }
            })
        );
        this._destroySubscriptions.push(
            this.finderConfig.events$.subscribe(event => this._finderEventHandler(event)),
            this.layoutConfig.events$.subscribe(event => this._layoutEventHandler(event)),
            this.keywordSearchConfig.events$.subscribe(event => this._keywordSearchEventHandler(event)),
            this.layoutMenuConfig.events$.subscribe(event => this._layoutMenuEventHandler(event)),
            this.displayConfig.events$.subscribe(event => this._displayEventHandler(event)),
            this.refinebyConfig.events$.subscribe(action => this._onSubscribeRefinebyEvents(action)),
            this.relationshipTreeConfig.events$.subscribe(event => this._relationshipTreeEventHandler(event)),
            this.threadDetailConfig.events$.subscribe(event => this._threadDetailEventHandler(event)),
            this.threadFilterConfig.eventSubject$.subscribe(event => this._threadFilterEventHandler(event)),
            this.threadListConfig.events$.subscribe(event => this._threadListEventHandler(event)),
            this.config.actionSubject$.subscribe(event => this._parentEventHandler(event)),
            this._relationshipTreeService.relationshipTreeOperate$.subscribe(event => this._updateScreenHandler(event)),
            this._appendEntitiesDialogService.actions$
                .pipe(filter(res => res.type === 'Reload'), debounceTime(50),
                    auditTime(100))
                .subscribe(res => {
                    this._isAppendEntities = true;
                    this.onRefreshClicked();
                })
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),

            this._store.pipe<string>(select('noteMsg'))
                .pipe(filter(res => res && res.length > 0))
                .subscribe(res => this._onSubscribeDeleteMsg(res)),

            this._store.pipe(
                select('screenInfo')
            ).subscribe(screenInfo => {
                if (screenInfo.type === 'CREATE_RESEARCH') {
                    this._isCreateScreen = true;
                }
            }),
        );

        this.threadListTotalCount$ = this._store.pipe(
            select(getDynamicResearchThreadListItemSelector2(this.screenKey)),
            map((threadEntries: DataList<ThreadEntry>) => {
                this._threadListIDs = Array.from(new Set(threadEntries.map(item => item.threadEntryThreadId))).join(';');
                return threadEntries.totalCount ? threadEntries.totalCount : 0;
            })
        );
        this.threadListTotalCount$.subscribe((count: number) => {
            this._threadListTotalCount = count;
        });

        this._changeDetectorRef.detectChanges();
    }

    ngAfterViewInit() {
        // TODO: need to find a better way to load top thread list.
        setTimeout(() => {
            // use to fix the "ExpressionChangedAfterItHasBeenCheckedError" happened in snippet-thread-list
            if (!this._changeDetectorRef['destroyed']) {
                this.threadListConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
            }
        });
        this.refinebyConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        // Add 'implements OnDestroy' to the class.
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onAppendClicked() {
        const _noteOption = new AppendEntitiesDialogOpenOptions();
        _noteOption.minimizable = true;
        _noteOption.selectedItems = [];
        _noteOption.openType = AppendEntitiesDialogType.AppendAllEntities;
        _noteOption.queryInfo = this._queryInfo;
        _noteOption.threadListTotalCount = this._threadListTotalCount;
        _noteOption.threadListIDs = this._threadListIDs;
        this._appendEntitiesDialogService.dialogOpen$.next(_noteOption);
    }

    onRefreshClicked() {
        if (this._refreshing) return;
        this._refreshing = true;

        this._refreshManually = true;
        this.refinebyConfig.actions$.next({
            type: 'RELOAD_DATA',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this.threadListConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        setTimeout(() => (this._refreshing = false), 500);
    }

    onSortSelect(e) {
        if (e.payload.data.sortBy) {
            this.sortInfo.sortBy = e.payload.data.sortBy;
        }
        if (e.payload.data.sortOrder) {
            this.sortInfo.sortOrder = e.payload.data.sortOrder;
        }

        this._store.dispatch(new ResearchScreenSetSortInfo(this.screenKey, this.sortInfo));

        this._queryInfo.sortBy = this.sortInfo.sortBy;
        this._queryInfo.sortOrder = this.sortInfo.sortOrder;
        this.threadListConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this._changeResearchScreenStatus();
        this._changeDetectorRef.detectChanges();
    }

    private _actionMessageHandler(action) {
        if (action.prefix === this.screenKey) {
            if (action.type === ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES) {
                this.isThreadDetailLoading = true;
            } else if (action.type === ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES_SUCCESS) {
                this.isThreadDetailLoading = false;
            }
        }

        if (action.type === UPDATE_NOTE_SUCCESS || action.type === CREATE_NOTE_SUCCESS ||
            action.type === DELETE_NOTE_SUCCESS || action.type === DELETE_NOTES_SUCCESS ||
            action.type === DELETE_EVENT_SUCCESS || action.type === UPDATE_EVENT_SUCCESS) {
            this._triggerDataLoad();
        }

        if (action.type === EDIT_CONTACT_SUCCESS) {
            this._triggerRelationshipTreeLoad(action.payload.id);
            this._triggerDataLoad();
        }

        if (action.type === DELETE_NOTE_SUCCESS || action.type === DELETE_SIDENOTE_SUCCESS || action.type === DELETE_NOTES_SUCCESS) {
            // popup notification from research screen if deleting note/sidenote in research view
            if (this.screenKey === businessConstants.researchScreen.defaultResearchScreenKey) {
                this._toast.notify({
                    message: businessConstants.note.deleteSuccessfully,
                    style: NotificationStyles.Success
                });
            }
        }

        if (action.type === ResearchScreenActionTypes.RELOAD_SUCCESS && this._refreshManually) {
            this._refreshManually = false;
            if (!this._isAppendEntities) {
                this._toast.notify({
                    message: 'Note list is refreshed.',
                    style: NotificationStyles.Info
                });
            } else {
                this._isAppendEntities = false;
            }
        }
        setTimeout(() => {
            this._changeDetectorRef.detectChanges();
        });
    }

    private _changeResearchScreenStatus(component = '') {
        if (this.screenKey !== businessConstants.researchScreen.defaultResearchScreenKey) {
            this._store.dispatch({
                type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                payload: {
                    screenKey: this.screenKey,
                    component: component
                }
            });
        } else {
            this._store.dispatch({
                type: ResearchScreenActionTypes.SET_SAVE_SCREEN_CHANGE_STATUS,
                payload: true
            });
        }
    }

    private _initRefineby() {
        this.refinebyConfig = new ResearchRefinebyConfig();
        this.refinebyConfig.keyInStore = this.screenKey;
        this.refinebyConfig.researchRefinebyLoadAction = ResearchScreenActionTypes.LOAD_REFINEBY;
        this.refinebyConfig.researchRefinebySetCategoryAction = ResearchScreenActionTypes.SET_REFINEBY_CATEGORY;
        this.refinebyConfig.researchRefinebySetCategoryOrderAction = ResearchScreenActionTypes.SET_REFINEBY_CATEGORY_ORDER;
        this.refinebyConfig.researchRefinebySetCategoryStatusAction = ResearchScreenActionTypes.SET_REFINEBY_CATEGORY_STATUS;
        this.refinebyConfig.researchRefinebySetSelectedValueAction = ResearchScreenActionTypes.SET_REFINEBY_SELECTED_VALUE;
        this.refinebyConfig.getResearchViewRefinebyDataStateSelector = getDynamicResearchRefinebyDataSelector(this.screenKey);
        this.refinebyConfig.getResearchViewRefinebyCategoryStateSelector = getDynamicResearchRefinebyCategorySelector(this.screenKey);
        this.refinebyConfig.getResearchViewRefinebySelectedValueStateSelector =
            getDynamicResearchRefinebySelectedValueSelector(this.screenKey);
        this.refinebyConfig.getResearchViewRefinebyStatusStateSelector = getDynamicResearchRefinebyStatusSelector(this.screenKey);
        this.refinebyConfig.getResearchViewRefinebyCategoryFilterStateSelector =
            getDynamicResearchRefinebyCategoryFilterSelector(this.screenKey);
    }

    private _initThreadFilter() {
        this.threadFilterConfig = new ThreadFilterConfig();
        this.threadFilterConfig.keyInStore = this.screenKey;
        this.threadFilterConfig.setFilterLogicAction = ResearchScreenActionTypes.SET_FILTER_LOGIC;
        this.threadFilterConfig.setFilterItemsAction = ResearchScreenActionTypes.SET_FILTER_LIST;
        this.threadFilterConfig.setFilterEnableAction = ResearchScreenActionTypes.SET_FILTER_ENABLE;
        this.threadFilterConfig.filterLogicSelector = getDynamicResearchFilterLogicSelector2(this.screenKey);
        this.threadFilterConfig.filterStatusSelector = getDynamicResearchFilterEnableSelector2(this.screenKey);
        this.threadFilterConfig.filterItemsSelector = getDynamicResearchFilterItemsSelector2(this.screenKey);
        // initialize the filterStatusSelector in the displayconfig for display component
        this.displayConfig.filterStatusSelector = this.threadFilterConfig.filterStatusSelector;
    }

    private _initThreadList() {
        this.threadListConfig = new ThreadListConfig();
        this.threadListConfig.keyInStore = this.screenKey;
        this.threadListConfig.setSelectedIndexAction = ResearchScreenActionTypes.SET_SELECTED_INDEX;
        this.threadListConfig.setGridRowModelTypeAction = ResearchScreenActionTypes.SET_GRIDG_ROW_MODEL_TYPE;
        this.threadListConfig.reloadAction = ResearchScreenActionTypes.RELOAD;
        this.threadListConfig.reloadGroupAction = ResearchScreenActionTypes.RELOAD_GROUP;
        this.threadListConfig.loadMoreAction = ResearchScreenActionTypes.LOAD_MORE;
        this.threadListConfig.setGridStatusAction = ResearchScreenActionTypes.SET_GRID_STATUS;
        this.threadListConfig.setQueryInfoAction = ResearchScreenActionTypes.SET_QUERY_INFO;
        this.threadListConfig.clearThreadListAction = ResearchScreenActionTypes.CLEAR_THREAD_LIST;
        this.threadListConfig.clearGroupByNoteItemsAction = ResearchScreenActionTypes.CLEAR_GROUP_BY_NOTE_ITEMS;
        this.threadListConfig.gridInfoSelector = getDynamicResearchGridStatusSelector2(this.screenKey);
        this.threadListConfig.queryInfoSelector = getDynamicResearchQueryInfoSelector2(this.screenKey);
        this.threadListConfig.sortInfoSelector = getDynamicResearchSortInfoSelector2(this.screenKey);
        this.threadListConfig.threadListSelector = getDynamicResearchThreadListSelector2(this.screenKey);
        this.threadListConfig.threadListItemSelector = getDynamicResearchThreadListItemSelector2(this.screenKey);
        this.threadListConfig.threadListGroupItemSelector = getDynamicResearchThreadListGroupItemSelector(this.screenKey);
        this.threadListConfig.threadListGroupItemsSelector = getDynamicResearchThreadListGroupItemsSelector(this.screenKey);
        this.threadListConfig.threadListGridGroupLayoutSelector = getDynamicResearchThreadListGridGropLayoutSelector(this.screenKey);
    }

    private _initThreadDetail() {
        this.threadDetailConfig = new ThreadDetailConfig();
        this.threadDetailConfig.keyInStore = this.screenKey;
        this.threadDetailConfig.dataStoreSelector = getDynamicResearchActiveThreadSelector2(this.screenKey);
        this.threadDetailConfig.keywordStoreSelector = getDynamicResearchKeywordSelector2(this.screenKey);
        this.threadDetailConfig.layoutSelector = getDynamicResearchLayoutSelector2(this.screenKey);
        this.threadDetailConfig.matchedAttachmentStoreSelector = getDynamicResearchThreadListtMatchedAttachmentSelector2(this.screenKey);
        this.threadDetailConfig.getNoteBodyActionName = ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES;
    }

    private _initSubComponentState() {
        // assign keyword value for home screen, the value of research screen is empty becuase the screenKey is always researchView
        const keywordState = this._storeQuerier.queryBySelector(getDynamicResearchKeywordSelector2(this.screenKey));
        if (keywordState) {
            this._queryInfo.searchText = StringHelperService.escapeSearchString(keywordState);
        }
        this.keywordSearchConfig = new KeywordSearchConfig(keywordState);

        const finderState = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
        this.finderConfig.initState = finderState;

        const relationshipTreeState = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipSelector2(this.screenKey));
        // for relationship tree init, there is something wrong for this issue
        // because when update the relationship tree, need to update all the relationship tree, including the treenodes, checkedkeys, expandkeys
        // however, when update the tree, just according to request, only update the related parts
        // and for the relationship store design, need to enhance, need to merge with nodes, checkedkeys, expandedkeys, showAs to one node
        // so, for this bug, we need to get the entity to get the new nodes to show, can not get the data from store
        this.isRelationshipLoading = true;
        if (this.finderConfig.initState.entity) {
            this._entityService.getEntityDetailWithoutAdhoc(this.finderConfig.initState.entity.id).pipe(
                take(1),
            ).subscribe(response => {
                const entity = Entity.parse(response);
                const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                const expandedKeys = treeNodes.filter(node => !node.isLeaf).map(node => node.uuid);
                this.relationshipTreeConfig.nodes = treeNodes;
                this.relationshipTreeConfig.checkedKeys = relationshipTreeState.checkedKeys;
                this.relationshipTreeConfig.showAs = relationshipTreeState.showAs;
                this.relationshipTreeConfig.checkable = true;
                this.relationshipTreeConfig.expandable = true;
                this.relationshipTreeConfig.expandedKeys = expandedKeys;
                this.isRelationshipLoading = false;
            });
        }

        this.sortInfo = this._storeQuerier.queryBySelector(getDynamicResearchSortInfoSelector2(this.screenKey));
        this.sortChangeSubject.next(this.sortInfo);

        // prepare query info
        if (finderState.status === ResearchFocusStatus.ALL) {
            this._queryInfo.entityIds = [];
        } else if (finderState.status === ResearchFocusStatus.NONE
            && relationshipTreeState.checkedKeys
            && relationshipTreeState.checkedKeys.length === 0) {
            this._queryInfo.entityIds = [finderState.entity.id];
        } else {
            this._queryInfo.entityIds = this._researchHelper.restoreEntityIdsFromTree(relationshipTreeState.checkedKeys,
                relationshipTreeState.nodes);
        }
        if (this.sortInfo) {
            this._queryInfo.sortBy = this.sortInfo.sortBy;
            this._queryInfo.sortOrder = this.sortInfo.sortOrder;
        }
        const isFilterOn = this._storeQuerier.queryBySelector(getDynamicResearchFilterEnableSelector2(this.screenKey));
        if (isFilterOn) {
            const filtersConfigs = this._storeQuerier.queryBySelector(getDynamicResearchFilterItemsSelector2(this.screenKey));
            const filterLogic = this._storeQuerier.queryBySelector(getDynamicResearchFilterLogicSelector2(this.screenKey));
            const advFitlerString = this._filterService.parseFilterSettingToAdvFilter(filtersConfigs, filterLogic, null,
                null, this._currentUser.id);
            this._queryInfo.advFilterMap.set('fitlerDialog', advFitlerString);
        }
        // display info
        const _displayState = this._storeQuerier.queryBySelector(getDynamicResearchDisplaySelector2(this.screenKey));
        if (_displayState) {
            if (_displayState.showAsMode !== undefined) {
                this._showAsMode = _displayState.showAsMode;
            } else {
                this._showAsMode = ShowAsMode.NOTES;
            }
            if (_displayState.showCalendarEvents !== undefined) {
                this._showCalendarEvents = _displayState.showCalendarEvents;
            }
        } else {
            this._showAsMode = ShowAsMode.NOTES;
        }

        if (this._showCalendarEvents) {
            this._queryInfo.advFilterMap.set('default', AppConfig.advFilterNotIncludeEmailSpooler);
        } else {
            this._queryInfo.advFilterMap.set('default', AppConfig.defaultAdvFilter);
        }
        this._queryInfo.showCalendarEvents = this._showCalendarEvents;
        this.displayConfig.showAsMode = this._showAsMode;
        // set the show as mode for thread detail
        this.threadDetailConfig.showAsMode = this._showAsMode;
        this.threadListConfig.showAsMode = this._showAsMode;
        this.displayConfig.showCalendarEvents = this._showCalendarEvents;
        this.displayConfig.actions$.next(_displayState);
        this._changeDetectorRef.detectChanges();
    }

    private _initTabs() {
        const menuItems = [
            new TabItem('Refine by'),
            new TabItem('Relationships'),
        ];
        // active tab maybe a number, a string or null
        const researchLayout = this._storeQuerier.queryBySelector(getDynamicResearchLayoutSelector2(this.screenKey));
        if (researchLayout && researchLayout.activeTab === businessConstants.research.saveScreen.refineByTab) {
            this.currentTab = 0;
        } else if (researchLayout && researchLayout.activeTab === businessConstants.research.saveScreen.relationshipTab) {
            this.currentTab = 1;
        } else {
            this.currentTab = 0;
        }

        this.tabsOptions = new TabOptions(menuItems, this.currentTab, false, { 'font-size': '14px', 'height': '30px' });

        this._destroySubscriptions.push(this.tabsOptions.events$.subscribe(event => {
            if (event.type === 'TAB_CHANGED') {
                this.currentTab = event.payload;
                if (this.currentTab === 1) {
                    this._store.dispatch(new ResearchScreenSetActiveTab(this.screenKey,
                        businessConstants.research.saveScreen.relationshipTab));
                } else {
                    this._store.dispatch(new ResearchScreenSetActiveTab(this.screenKey, businessConstants.research.saveScreen.refineByTab));
                }
                this._changeResearchScreenStatus();
            }
        }));
    }

    private _displayEventHandler(e) {
        if (e.type === CHANGE_SHOW_LIST_MODE) {
            const filterSelectedItemIndex = e.payload;
            if (filterSelectedItemIndex < 2) {
                this._showAsMode = filterSelectedItemIndex;
                // change the show as mode for thread detail
                this.threadDetailConfig.showAsMode = this._showAsMode;
                this.threadListConfig.showAsMode = this._showAsMode;
                this._store.dispatch(
                    new ResearchScreenSetDisplayInfo(this.screenKey, {
                        showAsMode: this._showAsMode,
                        showCalendarEvents: this._showCalendarEvents
                    })
                );
                this.displayConfig.showAsMode = this._showAsMode;

                this.refinebyConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
                this.threadListConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
                this._changeResearchScreenStatus();
            }
        } else if (e.type === SHOW_CALENDAR_EVENTS) {
            this._showCalendarEvents = e.payload;
            this._queryInfo.showCalendarEvents = this._showCalendarEvents;
            this._store.dispatch(
                new ResearchScreenSetDisplayInfo(this.screenKey, {
                    showAsMode: this._showAsMode,
                    showCalendarEvents: this._showCalendarEvents
                })
            );
            this.displayConfig.showCalendarEvents = this._showCalendarEvents;
            if (this._showCalendarEvents) {
                this._queryInfo.advFilterMap.set('default', AppConfig.advFilterNotIncludeEmailSpooler);
            } else {
                this._queryInfo.advFilterMap.set('default', AppConfig.defaultAdvFilter);
            }

            this.refinebyConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            this.threadListConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            this._changeResearchScreenStatus();
        } else if (e.type === OPEN_FILTER_DIALOG && !this.config.advFilter) {
            this._threadFilterService.dialogOpen$.next({
                type: OPEN_DIALOG,
                payload: this.threadFilterConfig
            });
        }
    }

    private _layoutMenuEventHandler(e) {
        const layoutMenuSelectedItemIndex = e.payload.id;
        if (layoutMenuSelectedItemIndex < 4) {
            this._store.dispatch(new ResearchScreenSetLayoutMode(this.screenKey, layoutMenuSelectedItemIndex));
            const layoutMode = this.layoutMenuConfig.layoutInfo.layoutMode;
            if (this._rowModelType === 'serverSide' &&
                (layoutMode === 3 || layoutMode === 2) &&
                layoutMenuSelectedItemIndex === 1) {
                setTimeout(() => {
                    this.threadListConfig.actions$.next({
                        type: 'RELOAD',
                        payload: {
                            queryInfo: this._queryInfo,
                            showAsMode: this._showAsMode
                        }
                    });
                });
            }
            this.layoutMenuConfig.layoutInfo.layoutMode = layoutMenuSelectedItemIndex;
            this.layoutConfig.actions$.next({
                type: ResearchLayoutActions.SET_LAYOUT,
                payload: layoutMenuSelectedItemIndex
            });
        }

        this._changeResearchScreenStatus();
        this._changeDetectorRef.detectChanges();
    }

    private _onSubscribeDeleteMsg(msg: string) {
        if (msg.indexOf(DELETE_NOTE_FAILURE) > -1 || msg.indexOf(DELETE_SIDENOTE_FAILURE) > -1) {
            this._toast.notify({
                message: msg,
                style: NotificationStyles.Info
            });
        }
        if (msg.indexOf(DELETE_ATTACHMENT_SUCCESS) > -1 || msg.indexOf(DELETE_SIDENOTE_SUCCESS) > -1) {
            this.refinebyConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            if (this._showAsMode === 0) {
                this.threadListConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
            }
        }
    }

    private _onFinderChanged(finder: FinderSetting) {
        const previousFinder: FinderSetting = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
        const showAsName = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipSelector2(this.screenKey)).showAs;
        if (previousFinder.entity.id !== finder.entity.id) {
            // focus entity changed
            this.isRelationshipLoading = true;
            this._queryInfo.entityIds = [finder.entity.id];
            this._entityService.getEntityDetailWithoutAdhoc(finder.entity.id).pipe(
                take(1),
            ).subscribe(response => {
                const entity = Entity.parse(response);
                const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                const expandedKeys = treeNodes.filter(node => !node.isLeaf).map(node => node.uuid);
                this._store.dispatch(new ResearchScreenSetRelationship(this.screenKey, {
                    nodes: treeNodes,
                    expandedKeys: expandedKeys,
                    checkedKeys: [treeNodes.filter(node => node.level === '1')[0].uuid],
                    showAs: showAsName
                }));
                this.relationshipTreeConfig.nodes = treeNodes;
                this.relationshipTreeConfig.checkable = true;
                this.relationshipTreeConfig.expandable = true;
                this.relationshipTreeConfig.checkedKeys = [treeNodes.filter(node => node.level === '1')[0].uuid];
                this.relationshipTreeConfig.expandedKeys = expandedKeys;
                this.isRelationshipLoading = false;
            });
        } else if (previousFinder.status === ResearchFocusStatus.ONE_DEGREE && finder.status === ResearchFocusStatus.NONE) {
            // focus entity keep; OneDegree -> None
            const treeNodes = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipTreeNodesSelector2(this.screenKey));
            if (this._previousRelTreeCheckedKeys.length === 0 || this._previousRelTreeCheckedKeys.length === treeNodes.length) {
                this.relationshipTreeConfig.checkedKeys = [];
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, []));
                this._queryInfo.entityIds = [finder.entity.id];
            } else {
                this.relationshipTreeConfig.checkedKeys = this._previousRelTreeCheckedKeys;
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, this._previousRelTreeCheckedKeys));
                this._queryInfo.entityIds = this._researchHelper.restoreEntityIdsFromTree(this._previousRelTreeCheckedKeys, treeNodes);
            }
            this._relationshipTreeService.relationshipTreeOperate$.next({
                type: RelationshipTreeActions.CHANGE_CHECKEDKEYS,
                payload: this.relationshipTreeConfig.checkedKeys
            });
        } else if (finder.status === ResearchFocusStatus.ONE_DEGREE) {
            // focus entity keep; None/All -> OneDegree
            const treeNodes = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipTreeNodesSelector2(this.screenKey));
            const checkKeys = treeNodes.map(node => node.uuid);
            this.relationshipTreeConfig.checkedKeys = checkKeys;
            this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, checkKeys));
            this._queryInfo.entityIds = treeNodes.filter(node => node.nodeType === 'ENTITY').map(node => node.id);
            this._relationshipTreeService.relationshipTreeOperate$.next({
                type: RelationshipTreeActions.CHANGE_CHECKEDKEYS,
                payload: this.relationshipTreeConfig.checkedKeys
            });
        } else if (finder.status === ResearchFocusStatus.ALL) {
            // focus entity keep; check All
            this._queryInfo.entityIds = [];
        } else {
            // focus entity keep; uncheck All
            const treeNodes = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipTreeNodesSelector2(this.screenKey));
            const checkedKeys = this._storeQuerier.queryBySelector(getDynamicResearchRelationshipTreeCheckedKeysSelector2(this.screenKey));
            if (!checkedKeys || checkedKeys.length === 0) {
                this._queryInfo.entityIds = [finder.entity.id];
            } else {
                this._queryInfo.entityIds = this._researchHelper.restoreEntityIdsFromTree(checkedKeys, treeNodes);
            }
        }
        this.refinebyConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this.threadListConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this._store.dispatch(new ResearchScreenInitializeFocus(this.screenKey, finder));
        this.finderConfig.initState = finder;
        this._changeDetectorRef.detectChanges();
    }

    private _finderEventHandler(event) {
        if (event.type === 'CHANGE') {
            this._onFinderChanged(event.payload);
            this._changeResearchScreenStatus();
        }
    }

    private _keywordSearchEventHandler(event) {
        if (event.type === 'CHANGE') {
            this._queryInfo.searchText = event.payload.data;
            this.refinebyConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            this.threadListConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            this._store.dispatch(new ResearchScreenSetKeyWord(this.screenKey, event.payload.state));
            this.keywordSearchConfig.keyword = event.payload.data;
            this._changeResearchScreenStatus(ResearchScreenSubComponents.KEYWORD);
        }
    }

    private _layoutEventHandler(event) {
        if (event.type === ResearchLayoutEvents.COLLAPSE_CHANGE) {
            this._store.dispatch(new ResearchScreenSetLayoutCollapse(this.screenKey, event.payload));
            this._changeResearchScreenStatus();
        } else if (event.type === ResearchLayoutEvents.LAYOUTMODE_CHANGE) {
            this._store.dispatch(new ResearchScreenSetLayoutMode(this.screenKey, event.payload));
            this._changeResearchScreenStatus();
        }
    }

    private _onRelationshipTreeCheckedNodeChanged(checkedKeysParam): void {
        const checkedKeys: string[] = checkedKeysParam.chekedKeys;
        const isTriggeredByTabChanged = checkedKeysParam.isTriggeredByTabChanged;
        // update focus "One Degree" status
        const finder = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
        const treeNodes = this.relationshipTreeConfig.nodes;

        if (!isTriggeredByTabChanged) {
            if (finder.status === ResearchFocusStatus.NONE && checkedKeys.length === treeNodes.length) {
                this.finderConfig.actions$.next({
                    type: FinderActions.SET_VALUE_STATUS,
                    payload: ResearchFocusStatus.ONE_DEGREE
                });
                this._store.dispatch(new ResearchScreenSetFocusStatus(this.screenKey, ResearchFocusStatus.ONE_DEGREE));
            } else {
                this.finderConfig.actions$.next({
                    type: FinderActions.SET_VALUE_STATUS,
                    payload: ResearchFocusStatus.NONE
                });
                this._store.dispatch(new ResearchScreenSetFocusStatus(this.screenKey, ResearchFocusStatus.NONE));
            }
            if (checkedKeys == null || checkedKeys.length === 0) {
                this._previousRelTreeCheckedKeys = [];
                this._queryInfo.entityIds = [finder.entity.id];
                this.refinebyConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
                this.threadListConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, []));
            } else {
                this._previousRelTreeCheckedKeys = checkedKeys;
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, checkedKeys));
                this._queryInfo.entityIds = this._researchHelper.restoreEntityIdsFromTree(checkedKeys, treeNodes);
                this.refinebyConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });

                this.threadListConfig.actions$.next({
                    type: 'RELOAD',
                    payload: {
                        queryInfo: this._queryInfo,
                        showAsMode: this._showAsMode
                    }
                });
            }
        }
    }

    private _onRelationshipTreeNodeDoubleClicked(node: RelationshipTreeNode) {
        const entity: EntityBrief = {
            id: node.id,
            name: node.name,
            shortName: node.shortName,
            type: node.type,
            isPublic: node.isPublic,
            company: node.company,
        };
        const newFinderValue: FinderSetting = {
            entity: entity,
            status: ResearchFocusStatus.NONE
        };
        this.finderConfig.actions$.next({
            type: 'SET_VALUE',
            payload: newFinderValue
        });
        this._onFinderChanged(newFinderValue);
    }

    private _onRelationshipTreeStatus(node: RelationshipTreeNode, previousFinderStatus) {
        const entity: EntityBrief = {
            id: node.id,
            name: node.name,
            shortName: node.shortName,
            type: node.type,
            isPublic: node.isPublic,
            company: node.company,
        };
        const newFinderValue: FinderSetting = {
            entity: entity,
            status: previousFinderStatus
        };
        this.finderConfig.actions$.next({
            type: 'SET_VALUE',
            payload: newFinderValue
        });
        this._onFinderChanged(newFinderValue);
    }

    private _onRelationshipTreeShowAsChanged(nameFormat: 'name' | 'shortName') {
        this.relationshipTreeConfig.showAs = nameFormat;
        // save relationship tree name format to store
        this._store.dispatch(new ResearchScreenSetRelationshipNameFormat(this.screenKey, nameFormat));
        this._resetRefinebyContextMenu();
    }

    private _relationshipTreeEventHandler(event) {
        if (event.type === RelationshipTreeEvents.CHECKED_NODE_CHANGED) {
            this._onRelationshipTreeCheckedNodeChanged(event.payload);
        } else if (event.type === RelationshipTreeEvents.CONTEXTMENU_VIEWRESEARCH) {
            this._onRelationshipTreeNodeDoubleClicked(event.payload);
        } else if (event.type === RelationshipTreeEvents.CONTEXTMENU_SHOWNAMEFORMAT) {
            this._onRelationshipTreeShowAsChanged(event.payload);
            // only changing name format need to trigger auto save
            this._changeResearchScreenStatus(ResearchScreenSubComponents.RELATIONSHIP_TREE);
        }
    }

    private _updateScreenHandler(event) {
        // when relationshipTree changed, the tree will reload
        // need to do: the checked keys cannot be updated
        if (event.type === RelationshipTreeActions.CHANGE_RELATIONSHIP
            && this.relationshipTreeConfig.nodes.findIndex(item => item.id === event.payload.rootNodeId) > -1) {
            // focus entity changed
            this.isRelationshipLoading = true;
            this._changeDetectorRef.detectChanges();
            let newRelationshipExpandkeys = '';
            // append the new relationship type expandkeys
            if (event.payload.clickedItem.type.id === EntityType.CONTACT.id) {
                newRelationshipExpandkeys = 'CONTACTS_GROUP-' + event.payload.targetRelationshipType.id;
            } else {
                newRelationshipExpandkeys = 'ENTITY_GROUP-' + event.payload.targetRelationshipType.id;
            }
            this._relationshipTreeService.updateRelationshipWithCheckedKeys(event, this.screenKey, this.relationshipTreeConfig)
                .subscribe(res => {
                    if (res !== 'error') {
                        if (event.payload.rootNodeId !== this.relationshipTreeConfig.nodes[0].id) {
                            this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, {
                                checkedKeys: []
                            }));
                            this.relationshipTreeConfig.checkedKeys = [];
                        }
                        this.relationshipTreeConfig.expandedKeys.push(newRelationshipExpandkeys);
                        this.isRelationshipLoading = false;
                        this._changeDetectorRef.detectChanges();
                    }
                });
        }

        if (event.type === RelationshipTreeActions.REMOVE_RELATIONSHIP
            && this.relationshipTreeConfig.nodes.findIndex(item => item.id === event.payload.rootNodeId) > -1) {
            const previousFinder: FinderSetting = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
            this.isRelationshipLoading = true;
            this._changeDetectorRef.detectChanges();
            // update remove item itself checkedkeys
            if (event.payload.removeItem.level === '4') {
                this.relationshipTreeConfig.checkedKeys = this.relationshipTreeConfig.checkedKeys.
                    filter(item => item !== event.payload.removeItem.uuid);
            }
            if (event.payload.removeItem.level === '3') {
                // need to remove item self and it's children
                this.relationshipTreeConfig.checkedKeys = this.relationshipTreeConfig.checkedKeys.filter(item =>
                    item !== event.payload.removeItem.uuid &&
                    item.substr(0, (event.payload.removeItem.uuid.length - 33)) !== event.payload.removeItem.uuid);
            }

            if (this._updateRemoveCheckedKeys(event.payload.removeItem.uuid)) {
                this._queryInfo.entityIds = [previousFinder.entity.id];
                this._entityService.getEntityDetailWithoutAdhoc(previousFinder.entity.id).pipe(
                    take(1),
                ).subscribe(response => {
                    const entity = Entity.parse(response);
                    const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                    this._store.dispatch(new ResearchScreenSetRelationshipNodes(this.screenKey, treeNodes));
                    this.relationshipTreeConfig.nodes = treeNodes;
                    if (previousFinder.status !== ResearchFocusStatus.ALL) {
                        this._onRelationshipTreeStatus(treeNodes[0], previousFinder.status);
                    }
                    this.isRelationshipLoading = false;
                    this._changeDetectorRef.detectChanges();
                });
            }
        }
        if ((event.type === RelationshipTreeActions.ADD_RELATIONSHIP)
            && this.relationshipTreeConfig.nodes.findIndex(item => item.id === event.payload.rootNodeId) > -1) {
            let _currentRelTreeCheckedKeys = [];
            const tempCheckedKeys = [];
            const previousFinder: FinderSetting = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
            // focus entity changed
            this.isRelationshipLoading = true;
            this._changeDetectorRef.detectChanges();
            this._queryInfo.entityIds = [previousFinder.entity.id];
            this._entityService.getEntityDetailWithoutAdhoc(previousFinder.entity.id).pipe(
                take(1),
            ).subscribe(response => {
                const entity = Entity.parse(response);
                const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                // to get uuid
                treeNodes.forEach(item => {
                    if (event.payload.relationshipTypes &&
                        event.payload.relationshipTypes.includes(item.uuid.slice(-32))) {
                        tempCheckedKeys.push(item.uuid);
                    }
                });
                // add the orignial checked keys to the _currentRelTreeCheckedKeys
                _currentRelTreeCheckedKeys = this.relationshipTreeConfig.checkedKeys;

                if (previousFinder.status !== ResearchFocusStatus.ONE_DEGREE) {
                    // if it's not one-degree, we need to remove level2 and level 1 node's checkedkeys (not include parent)
                    _currentRelTreeCheckedKeys = _currentRelTreeCheckedKeys.filter(item => !tempCheckedKeys.includes(item)
                        && item !== tempCheckedKeys[0].substr(0, tempCheckedKeys[0].length - 33));
                } else {
                    // if it's one-degree, we need to add the newly added entity checkedkeys
                    _currentRelTreeCheckedKeys = [];
                    treeNodes.forEach(element => {
                        _currentRelTreeCheckedKeys.push(element.uuid);
                    });
                }
                // update store
                this._store.dispatch(new ResearchScreenSetRelationshipNodes(this.screenKey, treeNodes));
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, _currentRelTreeCheckedKeys));
                this.relationshipTreeConfig.nodes = treeNodes;
                this.relationshipTreeConfig.checkedKeys = _currentRelTreeCheckedKeys;

                this.relationshipTreeConfig.expandedKeys = treeNodes.filter(node => !node.isLeaf).map(node => node.uuid);

                if (previousFinder.status !== ResearchFocusStatus.ALL) {
                    this._onRelationshipTreeStatus(treeNodes[0], previousFinder.status);
                }
                this.isRelationshipLoading = false;
                this._changeDetectorRef.detectChanges();
            });
        }

        if (event.type === RelationshipTreeActions.EDIT_DETAILS
            && this.relationshipTreeConfig.nodes.findIndex(item => event.payload && item.id === event.payload.itemId) > -1) {
            const previousFinder: FinderSetting = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
            this.isRelationshipLoading = true;
            this._changeDetectorRef.detectChanges();
            this._queryInfo.entityIds = [previousFinder.entity.id];
            this._entityService.getEntityDetailWithoutAdhoc(previousFinder.entity.id).pipe(
                take(1),
            ).subscribe(response => {
                const entity = Entity.parse(response);
                const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                this.relationshipTreeConfig.nodes = treeNodes;
                this._onRelationshipTreeStatus(treeNodes[0], previousFinder.status);
                this.isRelationshipLoading = false;
                this._changeDetectorRef.detectChanges();
            });
        }
    }

    /**
    *  this function will update item self checkedkeys and it's parent checkedkeys
    *  if item is relationship type, need to handle children checkedkeys before call this function
    *
    * @private
    * @param {uuid}
    */
    private _updateRemoveCheckedKeys(uuid) {
        const currentRemoveItem = this.relationshipTreeConfig.nodes.filter(node => node.uuid === uuid)[0];
        let siblingNodeCount = 0;
        let siblingCheckedCount = 0;
        // here is need to handle one special case:
        // if the relationship type has only one node, we need to remove the item checkedkeys and relationship type checkedkey
        // if has more than one node, we just need to remove only item checkedkeys

        this.relationshipTreeConfig.nodes.forEach(node => {
            if (currentRemoveItem && node.parentId === currentRemoveItem.parentId) {
                siblingNodeCount++;
            }
        });

        if (siblingNodeCount === 1) {
            // only the item self, need to remove relationship type checkedkey
            // only item can remove it's parent relationship type checkedkey
            this.relationshipTreeConfig.checkedKeys = this.relationshipTreeConfig.checkedKeys.filter(checkedkey =>
                checkedkey !== currentRemoveItem.uuid.substring(0, currentRemoveItem.uuid.length - 33));
        } else {
            // to update relationship type checkedkeys
            // because if has two node, one is checked, another one is no, if we remove the on checked node, the relationship type
            // need to check too, so we need to update the relationship type checkedkey
            this.relationshipTreeConfig.checkedKeys.forEach(checkedkey => {
                if (currentRemoveItem && checkedkey.substring(0, (checkedkey.length - 33)) === currentRemoveItem.parentId) {
                    siblingCheckedCount++;
                }
            });
            if (siblingNodeCount - siblingCheckedCount === 1) {
                this.relationshipTreeConfig.checkedKeys.push(currentRemoveItem.uuid.substring(0, currentRemoveItem.uuid.length - 33));
            }
        }
        // TODO: Handle a special case [TAM-30359]
        if (currentRemoveItem) {
            if (currentRemoveItem.level === '1') {
                this._store.dispatch(new ResearchScreenSetRelationshipCheckedKeys(this.screenKey, this.relationshipTreeConfig.checkedKeys));
                return true;
            } else {
                return this._updateRemoveCheckedKeys(currentRemoveItem.parentId);
            }
        }
        return true;
    }

    private _resetRefinebyContextMenu() {
        // disable relstionship/entityFocus, 1. Layout disable them, 2. Special page disable them. (Special page: embed as tabs, email digest link)
        this.refinebyConfig.contextMenuItems = [
            { id: '0', text: 'Show Relationship Tree', checked: !this.isRelationshipTreeHidden, disabled: this.config.embedMode || this.isRelationshipTreeStatusDisable || !!this.config.advFilter },
            { id: '1', text: 'Show Focus', checked: !this.isEntityFocusHidden, disabled: this.config.embedMode || this.isEntityFocusStatusDisable || !!this.config.advFilter },
            { id: '2', text: 'Show as Shortname', checked: this.relationshipTreeConfig.showAs !== 'name' },
            { id: '3', text: 'Show as Name', checked: this.relationshipTreeConfig.showAs === 'name' },
        ];
        this.refinebyConfig.showAsName = this.relationshipTreeConfig.showAs === 'name';
        this.refinebyConfig.actions$.next({
            type: 'RESET_STATUS',
        });
    }

    private _setupLayout() {
        const layout = this._storeQuerier.queryBySelector(getDynamicResearchLayoutSelector2(this.screenKey));
        if (layout) {
            this.layoutConfig.layoutMode = layout.layoutMode;
            if (!this._isCreateScreen) {
                this.layoutConfig.isCollapsed = layout.collapsed;
            } else {
                delete this.layoutConfig.isCollapsed;
            }
            if (layout.layoutMode) {
                this.layoutMenuConfig.layoutInfo.layoutMode = layout.layoutMode;
            }

            this.isEntityFocusHidden = layout.isEntityFocusHidden;
            this.isRelationshipTreeHidden = layout.isRelationshipTreeHidden;
            this.isEntityFocusStatusDisable = layout.isEntityFocusDisable;
            this.isRelationshipTreeStatusDisable = layout.isRelationshipTreeDisable;

            this.layoutMenuConfig.actions$.next(this.layoutMenuConfig);
        }
    }

    private _onSubscribeRefinebyEvents(action) {
        if (action.type === 'UPDATE') {
            this._queryInfo.categoryFilter = action.payload.categoryFilter;
            this._queryInfo.category = action.payload.category;
            this.refinebyConfig.actions$.next({
                type: 'UPDATE',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
            this.threadListConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
        } else if (action.type === 'LOAD_REFINEBY') {
            this._queryInfo.categoryFilter = action.payload.categoryFilter;
            this._queryInfo.category = action.payload.category;
            this.refinebyConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
        } else if (action.type === 'RELATIONSHIP_TREE_STATUS') {
            this.isRelationshipTreeHidden = !action.payload;
            // update store for relationship tree status
            this._store.dispatch(new ResearchScreenHideRelationshipTree(this.screenKey, this.isRelationshipTreeHidden));
            this._resetRefinebyContextMenu();
            // change status to show the saving button
            this._changeResearchScreenStatus();
        } else if (action.type === 'FINDER_STATUS') {
            this.isEntityFocusHidden = !action.payload;
            // update store for entity focus status
            this._store.dispatch(new ResearchScreenHideEntityFocus(this.screenKey, this.isEntityFocusHidden));
            this._resetRefinebyContextMenu();
            // change status to show the saving button
            this._changeResearchScreenStatus();
        } else if (action.type === 'RELOAD') {
            this._queryInfo.categoryFilter = action.payload.categoryFilter;
            this._queryInfo.category = action.payload.category;
            this.refinebyConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });

            this.threadListConfig.actions$.next({
                type: 'RELOAD',
                payload: {
                    queryInfo: this._queryInfo,
                    showAsMode: this._showAsMode
                }
            });
        } else if (action.type === 'SHOW_AS_NAME') {
            this._onRelationshipTreeShowAsChanged(action.payload ? 'name' : 'shortName');
            this.relationshipTreeConfig.actions$.next({
                type: RelationshipTreeActions.RESET,
            });
        }
    }

    private _threadDetailEventHandler(event) {
        if (event.type === 'DELETE_ATTACHMENT') {
            // update thread item attachment
            this._store.dispatch(new ResearchScreenThreadListRemoveAttachment(this.screenKey, event.payload));
        } else if (event.type === 'DELETE_SIDENOTE') {
            // update thread item
            this._store.dispatch(new ResearchScreenThreadListRemoveSidenote(this.screenKey, event.payload));
        }
    }

    private _threadFilterEventHandler(event) {
        if (event.type === ThreadFilterEventType.apply) {
            // Gets data from store
            const filtersConfigs = this._storeQuerier.queryBySelector(getDynamicResearchFilterItemsSelector2(this.screenKey));
            const filterLogic = this._storeQuerier.queryBySelector(getDynamicResearchFilterLogicSelector2(this.screenKey));
            const advFitlerString = this._filterService.parseFilterSettingToAdvFilter(filtersConfigs, filterLogic, null,
                null, this._currentUser.id);
            this._queryInfo.advFilterMap.set('fitlerDialog', advFitlerString);
        } else if (event.type === ThreadFilterEventType.suspend) {
            this._queryInfo.advFilterMap.delete('fitlerDialog');
        }

        this.refinebyConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });

        this.threadListConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this._changeResearchScreenStatus();
    }

    private _threadListEventHandler(event) {
        if (event.type === 'SORTCHANGEED') {
            this.sortInfo = event.payload;
            this._store.dispatch(new ResearchScreenSetSortInfo(this.screenKey, this.sortInfo));
            this._changeResearchScreenStatus();
        } else if (event.type === 'DOUBLE_CLICK' || event.type === 'MAXIMIZE') {
            this.threadDetailConfig.actions$.next({
                type: ThreadDetailActions.TOGGLE_MAXIMIZE,
                payload: true
            });
        } else if (event.type === 'GRIDINFOCHANGED') {
            this._changeResearchScreenStatus();
        } else if (event.type === 'GRIDROWGROUPCHANGED') {
            this._changeResearchScreenStatus();
        }
    }

    private _triggerDataLoad() {
        // delay 1000ms if searchText is not empty, because lucene need some time to search the new item.
        if (this._queryInfo?.searchText.length > 0) {
            setTimeout(() => {
                this._triggerDataLoadActions();
            }, 1000);
        } else {
            this._triggerDataLoadActions();
        }
    }

    private _triggerDataLoadActions(): void {
        this.refinebyConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
        this.threadListConfig.actions$.next({
            type: 'RELOAD',
            payload: {
                queryInfo: this._queryInfo,
                showAsMode: this._showAsMode
            }
        });
    }

    // we need to handle the checkedkeys later
    private _triggerRelationshipTreeLoad(id) {
        if (this.relationshipTreeConfig.nodes.findIndex(item => item.id === id) > -1) {
            const previousFinder: FinderSetting = this._storeQuerier.queryBySelector(getDynamicResearchFocusSelector2(this.screenKey));
            this.isRelationshipLoading = true;
            this._changeDetectorRef.detectChanges();
            this._queryInfo.entityIds = [previousFinder.entity.id];
            this._entityService.getEntityDetailWithoutAdhoc(previousFinder.entity.id).pipe(
                take(1),
            ).subscribe(response => {
                const entity = Entity.parse(response);
                const treeNodes = RelationshipTreeService.parseToTreeViewModel(entity);
                this.relationshipTreeConfig.nodes = treeNodes;
                this.relationshipTreeConfig.expandedKeys = treeNodes.filter(node => !node.isLeaf).map(node => node.uuid);
                this._onRelationshipTreeStatus(treeNodes[0], previousFinder.status);
                this.isRelationshipLoading = false;
                this._changeDetectorRef.detectChanges();
            });
        }
    }

    private _parentEventHandler(action: ResearchScreenConfigAction) {
        // cleanup should trigger parent reload, non cleanup trigger this component reload
        if (action.type === ResearchScreenConfigActionType.Filter) {
            this.keywordSearchConfig.actions$.next({ type: 'SET', payload: action.payload.searchText });
        } else if (action.type === ResearchScreenConfigActionType.FOCUS_ON_ROW) {
            this.threadListConfig.actions$.next({ type: 'FOCUS' });
        } else if (action.type === ResearchScreenConfigActionType.TAB_CLICK) {
            this.threadListConfig.actions$.next({ type: 'TAB_CLICK' });
        } else if (action.type === ResearchScreenConfigActionType.PRINT) {
            this.threadListConfig.actions$.next(action);
        } else if (action.type === ResearchScreenConfigActionType.FOCUS_FINDER) {
            if (!this.isEntityFocusHidden && !this.config.advFilter) {
                this.finderConfig.actions$.next(action);
            }
        }
    }
}
